import { useCallback, useMemo, useState } from "react";

export interface WordCursor {
  /** Id of the focused word, or undefined when the page has no words. */
  activeId: string | undefined;
  /** Attach to the content element to make the erasure keyboard-operable. */
  containerProps: {
    role: "listbox";
    "aria-label": string;
    "aria-activedescendant": string | undefined;
    tabIndex: 0;
    onKeyDown: (event: React.KeyboardEvent) => void;
  };
  setActiveId: (id: string) => void;
}

interface Options {
  /**
   * The words' ids in reading order. Not the page: the page is new on every
   * mark, and deriving the ids from it rebuilt a list and a set of every word
   * on the page each time.
   */
  wordIds: readonly string[];
  onToggle: (id: string) => void;
  onEdit: (id: string) => void;
}

/**
 * Keyboard navigation across the erasure.
 *
 * Marking was previously pointer-only, so the app could not be used without a
 * mouse or touchscreen. Rather than making every one of potentially thousands of
 * words tabbable, this follows the roving-focus listbox pattern: the content
 * region takes a single tab stop, arrow keys move a cursor within it, and
 * `aria-activedescendant` tells assistive technology which word is current.
 */
export const useWordCursor = ({ wordIds, onToggle, onEdit }: Options): WordCursor => {
  const knownIds = useMemo(() => new Set(wordIds), [wordIds]);
  const [requestedId, setActiveId] = useState<string | undefined>(undefined);

  // Derived rather than corrected in an effect: if the requested word is gone
  // (or none has been chosen yet) the cursor falls back to the first word, with
  // no intermediate render showing a stale cursor.
  const activeId = requestedId && knownIds.has(requestedId) ? requestedId : wordIds[0];

  const moveBy = useCallback(
    (delta: number) => {
      if (wordIds.length === 0) return;
      const current = activeId ? wordIds.indexOf(activeId) : -1;
      const next = Math.min(wordIds.length - 1, Math.max(0, current + delta));
      setActiveId(wordIds[next]);
    },
    [activeId, wordIds],
  );

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // Undo/redo shortcuts belong to the toolbar's global handler.
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          event.preventDefault();
          moveBy(1);
          return;
        case "ArrowLeft":
        case "ArrowUp":
          event.preventDefault();
          moveBy(-1);
          return;
        case "Home":
          event.preventDefault();
          setActiveId(wordIds[0]);
          return;
        case "End":
          event.preventDefault();
          setActiveId(wordIds[wordIds.length - 1]);
          return;
        case " ":
        case "Enter":
          if (!activeId) return;
          event.preventDefault();
          onToggle(activeId);
          return;
        case "e":
          if (!activeId) return;
          event.preventDefault();
          onEdit(activeId);
          return;
        default:
      }
    },
    [activeId, moveBy, onEdit, onToggle, wordIds],
  );

  return {
    activeId,
    setActiveId,
    containerProps: {
      role: "listbox",
      "aria-label":
        "Erasure text. Arrow keys move between words, space erases, E edits letters.",
      "aria-activedescendant": activeId ? `word-${activeId}` : undefined,
      tabIndex: 0,
      onKeyDown,
    },
  };
};
